/**
 * Job Grouping Utilities
 *
 * Provides functions and constants for grouping job instances by
 * Job Site → Job → Instance hierarchy.
 *
 * Note: For contractors, jobs are grouped by Job Site only (not Client).
 * Clients are a corporate-only concept.
 */

/**
 * Alternating color scheme for job site groups
 * Creates visual depth: jobSite (darkest) → job → instance (lightest)
 */
export const JOB_SITE_COLORS = {
  odd: {
    jobSite: 'bg-background',
    job: 'bg-surface',
    instance: 'bg-surface-light'
  },
  even: {
    jobSite: 'bg-background-alt',
    job: 'bg-surface-alt',
    instance: 'bg-surface-alt-light'
  }
};

// Legacy alias for backward compatibility
export const RECEIVER_COLORS = JOB_SITE_COLORS;

/**
 * Separator styles for visual hierarchy
 */
export const SEPARATOR_STYLES = {
  jobSite: 'border-b-2 border-accent',
  job: 'border-b border-dashed border-accent/40',
  instance: 'border-b border-dotted border-gray-600/50'
};

/**
 * Resolves the job site display info from a job instance
 * Supports multiple data formats:
 * - instance?.jobs (contractor today/upcoming pages)
 * - instance?.job_instance?.jobs (contractor earnings/timesheets)
 * - instance?.job (corporate reports page)
 *
 * Note: Contractors see Job Sites only, not Clients.
 * The job_site contains address and contact info.
 */
export const getJobSiteInfo = (instance) => {
  const job = instance?.jobs || instance?.job_instance?.jobs || instance?.job;

  if (!job) {
    return { id: 'unknown', name: 'Unknown', type: 'unknown' };
  }

  // Jobs with job_site (new structure)
  // Note: site_name is the database field, but it may come as site_name or siteName depending on service
  if (job.job_site && job.job_site.id) {
    const siteName = job.job_site.site_name || job.job_site.siteName;
    const address = job.job_site.address;
    // Display format: "Site Name - Address" or just address if no name
    const displayName = siteName && address
      ? `${siteName} - ${address}`
      : siteName || address || 'Unknown Location';

    return {
      id: job.job_site.id,
      name: displayName,
      siteName: siteName,
      type: 'job_site',
      address: address,
      contact_person: job.job_site.contact_person,
      contact_phone: job.job_site.contact_phone
    };
  }

  // Legacy: Jobs with service_receiver (fallback for old data)
  if (job.service_receiver && job.service_receiver.id) {
    return {
      id: job.service_receiver.id,
      name: job.service_receiver.receiver_name || 'Unknown Receiver',
      type: job.service_receiver.receiver_type || 'company',
      // Map legacy fields
      address: job.location_address,
      contact_person: job.service_receiver.contact_person,
      contact_phone: job.service_receiver.contact_phone
    };
  }

  // Fallback: Jobs with location_address but no job_site or service_receiver
  // This handles jobs created before proper job_site assignment
  const locationAddress = job.location_address || job.original_address;
  if (locationAddress) {
    return {
      id: `location-${job.id || 'unknown'}`,
      name: locationAddress,
      type: 'job_site',
      address: locationAddress,
      contact_person: job.contact_person_name,
      contact_phone: job.contact_person_phone
    };
  }

  // Final fallback for jobs without any location info
  return { id: 'unassigned', name: 'Unassigned', type: 'unassigned' };
};

// Legacy alias for backward compatibility
export const getReceiverInfo = getJobSiteInfo;

/**
 * Groups job instances by Job Site → Job → Instance
 *
 * @param {Array} instances - Array of job instances with nested job data
 * @returns {Map} Hierarchical map: jobSiteId -> { jobSite, jobs: Map<jobId, { job, instances }> }
 */
export const groupInstancesByJobSiteAndJob = (instances) => {
  if (!instances?.length) return new Map();

  const groupedMap = new Map();

  instances.forEach((instance) => {
    const jobSite = getJobSiteInfo(instance);
    // Support multiple data formats: jobs, job_instance.jobs, or job
    const job = instance?.jobs || instance?.job_instance?.jobs || instance?.job;
    const jobId = job?.id || 'unknown';

    // Initialize job site group if needed
    if (!groupedMap.has(jobSite.id)) {
      groupedMap.set(jobSite.id, {
        jobSite,
        // Legacy alias for backward compatibility
        receiver: jobSite,
        jobs: new Map()
      });
    }

    const jobSiteGroup = groupedMap.get(jobSite.id);

    // Initialize job group within job site if needed
    if (!jobSiteGroup.jobs.has(jobId)) {
      jobSiteGroup.jobs.set(jobId, {
        job,
        instances: []
      });
    }

    // Add instance to job group
    jobSiteGroup.jobs.get(jobId).instances.push(instance);
  });

  // Sort job sites: Alphabetically, Unassigned last
  const sortedMap = new Map(
    [...groupedMap.entries()].sort(([idA, a], [idB, b]) => {
      if (idA === 'unassigned') return 1;
      if (idB === 'unassigned') return -1;
      return a.jobSite.name.localeCompare(b.jobSite.name);
    })
  );

  return sortedMap;
};

// Legacy alias for backward compatibility
export const groupInstancesByReceiverAndJob = groupInstancesByJobSiteAndJob;

/**
 * Groups timesheets by Job Site → Job → Timesheet
 * Similar to instance grouping but for earnings data
 *
 * @param {Array} timesheets - Array of timesheet records with nested job instance data
 * @returns {Map} Hierarchical map for earnings display
 */
export const groupTimesheetsByJobSiteAndJob = (timesheets) => {
  if (!timesheets?.length) return new Map();

  const groupedMap = new Map();

  timesheets.forEach((timesheet) => {
    const jobSite = getJobSiteInfo(timesheet);
    const job = timesheet?.job_instance?.jobs;
    const jobId = job?.id || 'unknown';

    if (!groupedMap.has(jobSite.id)) {
      groupedMap.set(jobSite.id, {
        jobSite,
        // Legacy alias for backward compatibility
        receiver: jobSite,
        jobs: new Map(),
        totalEarnings: 0,
        totalHours: 0
      });
    }

    const jobSiteGroup = groupedMap.get(jobSite.id);
    jobSiteGroup.totalEarnings += parseFloat(timesheet.amount_earned) || 0;
    jobSiteGroup.totalHours += parseFloat(timesheet.total_hours) || 0;

    if (!jobSiteGroup.jobs.has(jobId)) {
      jobSiteGroup.jobs.set(jobId, {
        job,
        timesheets: [],
        totalEarnings: 0,
        totalHours: 0
      });
    }

    const jobGroup = jobSiteGroup.jobs.get(jobId);
    jobGroup.timesheets.push(timesheet);
    jobGroup.totalEarnings += parseFloat(timesheet.amount_earned) || 0;
    jobGroup.totalHours += parseFloat(timesheet.total_hours) || 0;
  });

  // Sort job sites: Alphabetically, Unassigned last
  const sortedMap = new Map(
    [...groupedMap.entries()].sort(([idA, a], [idB, b]) => {
      if (idA === 'unassigned') return 1;
      if (idB === 'unassigned') return -1;
      return a.jobSite.name.localeCompare(b.jobSite.name);
    })
  );

  return sortedMap;
};

// Legacy alias for backward compatibility
export const groupTimesheetsByReceiverAndJob = groupTimesheetsByJobSiteAndJob;

/**
 * Counts total instances across all jobs in a job site group
 */
export const countJobSiteInstances = (jobSiteGroup) => {
  let count = 0;
  jobSiteGroup.jobs.forEach((jobGroup) => {
    count += jobGroup.instances?.length || jobGroup.timesheets?.length || 0;
  });
  return count;
};

// Legacy alias for backward compatibility
export const countReceiverInstances = countJobSiteInstances;

/**
 * Format time string for display (e.g., "09:00" -> "9:00 AM" or "09:00")
 * @param {string} timeString - Time in HH:MM format
 * @param {string} format - '12h' or '24h' (default: '12h')
 */
export const formatTime = (timeString, format = '12h') => {
  if (!timeString) return 'TBD';

  try {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);

    if (format === '24h') {
      return `${hours}:${minutes}`;
    }

    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch {
    return timeString;
  }
};

/**
 * Format date for display with smart relative formatting
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {string} format - 'DD/MM/YYYY', 'MM/DD/YYYY', or 'YYYY-MM-DD' (default: 'DD/MM/YYYY')
 */
export const formatDate = (dateString, format = 'DD/MM/YYYY') => {
  if (!dateString) return 'TBD';

  const date = new Date(dateString + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) {
    return 'Today';
  }

  if (date.getTime() === tomorrow.getTime()) {
    return 'Tomorrow';
  }

  // Format based on user preference
  const day = date.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const showYear = year !== today.getFullYear();

  switch (format) {
    case 'MM/DD/YYYY':
      return showYear ? `${month} ${day}, ${year}` : `${month} ${day}`;
    case 'YYYY-MM-DD':
      return showYear ? `${year} ${month} ${day}` : `${month} ${day}`;
    case 'DD/MM/YYYY':
    default:
      return showYear ? `${day} ${month} ${year}` : `${day} ${month}`;
  }
};

/**
 * Get color scheme based on index (alternating odd/even)
 */
export const getColorScheme = (index) => {
  return index % 2 === 0 ? RECEIVER_COLORS.even : RECEIVER_COLORS.odd;
};
